import { locationGroupApi } from '@/api/account-management/locationGroupApi';
import { type LocationGroup } from '@/models/account-management/LocationGroup';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DataGrid, type GridColDef, type GridRowParams } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { Perm } from '../../permissions/permissions';
import { usePermissions } from '../../permissions/usePermissions';
import { fillHeight } from '../../theme';
import DataGridDeleteButton from '../common/data-grid/DataGridDeleteButton';
import DataGridEditButton from '../common/data-grid/DataGridEditButton';
import Spacer from '../common/Spacer';
import { useToast } from '../common/toast/ToastProvider';
import LocationGroupDialog from './dialogs/LocationGroupDialog';

const LocationGroups = () => {
    const qc = useQueryClient();
    const toast = useToast();
    const strings = useLocalizedStrings();
    const app = useAppContext();
    const perms = usePermissions();
    const canCreate = perms.canCreate(Perm.configurationLocationGroups);
    const canUpdate = perms.canUpdate(Perm.configurationLocationGroups);
    const canDelete = perms.canDelete(Perm.configurationLocationGroups);

    // Scope comes from the global top-bar selector.
    const brandId = app.brandId ?? undefined;

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState<LocationGroup | null>(null);

    const { data: groups, isPending } = useQuery<LocationGroup[]>({
        queryKey: ['location-groups', brandId ?? ''],
        enabled: !!brandId,
        queryFn: () => locationGroupApi.list(brandId!),
        staleTime: 0,
    });

    const { mutateAsync: deleteGroup } = useMutation({
        mutationFn: (id: string) => locationGroupApi.delete(id),
        onSuccess: async () => { toast({ severity: 'success', message: strings.locationGroupDeleted }); await qc.invalidateQueries({ queryKey: ['location-groups', brandId ?? ''] }); },
        onError: () => toast({ severity: 'error', message: strings.locationGroupDeleteError }),
    });

    const openEdit = (g: LocationGroup) => { setEditingGroup(g); setDialogOpen(true); };

    const columns: GridColDef<LocationGroup>[] = [
        { field: 'name', headerName: strings.name, flex: 2 },
        { field: 'description', headerName: strings.description, flex: 3 },
        { field: 'priority', headerName: strings.priority, flex: 1, type: 'number' },
        { field: 'locationCount', headerName: strings.locations, flex: 1, valueGetter: (_value, row) => row.members?.length ?? 0 },
        {
            field: 'actions', headerName: strings.actions, type: 'actions', flex: 1,
            getActions: (params: GridRowParams<LocationGroup>) => [
                <DataGridEditButton id={params.id.toString()} onClick={() => openEdit(params.row)} aria-label="edit location group" readOnly={!canUpdate} />,
                <DataGridDeleteButton id={params.id.toString()} getWarningMessage={(id) => strings.locationGroupDeleteConfirm.replace('{{name}}', groups?.find(g => g.id === id)?.name ?? '')} onClick={(id) => deleteGroup(id)} aria-label="delete location group" hidden={!canDelete} />,
            ],
        },
    ];

    if (!brandId) {
        return (
            <Box sx={{ ...fillHeight, p: 2 }}>
                <Typography variant="body2" color="text.secondary">{strings.feedbackSelectBrandTopBar}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ ...fillHeight, overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="h6">{strings.locationGroups}</Typography>
                {canCreate && <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingGroup(null); setDialogOpen(true); }} size="input">{strings.locationGroupNew}</Button>}
            </Stack>
            <Spacer y={1} />
            <Box sx={{ flex: 1, minHeight: 0 }}>
                <DataGrid
                    rows={groups ?? []}
                    columns={columns}
                    autoPageSize
                    loading={isPending}
                    localeText={{ noRowsLabel: strings.locationGroupNoGroups }}
                />
            </Box>

            <LocationGroupDialog
                open={dialogOpen}
                brandId={brandId}
                group={editingGroup}
                readOnly={!!editingGroup && !canUpdate}
                onClose={() => setDialogOpen(false)}
            />
        </Box>
    );
};

export default LocationGroups;
